// Datafeed for COP-en. Én kilde-uavhengig strøm av Track[].
//
// To implementasjoner:
//  * LiveFeed — kobler til SKADE-backendens WebSocket og mottar
//    no.gna.spark.sensor-events (samme kontrakt som GNÅ/relay produserer),
//    normaliserer via @skade/contracts og emitter Track[]. Ingen mock.
//  * DemoFeed — genererer ÆRLIG merkede demo-events lokalt slik at UI-et kan
//    vises/utvikles uten backend. Demo-tracks er tydelig merket source:'demo'.
import {
  parseSensorBatch, sensorEventToTracks, Backoff, type Track,
  type CollabIn, type CollabMessage, parseCollabNack, type SporUtenPosisjon,
} from '@skade/contracts';
import { Sendeko, koTekst, politikkFor, nettlagring, type KoStatus } from './sendeko.js';

export type TrackListener = (tracks: Track[]) => void;
export type HistoryListener = (history: Record<string, [number, number][]>) => void;
export type CollabListener = (msg: CollabMessage) => void;
/** Ferske uposisjonerte deteksjoner (ukalibrert kilde, VALE SENS-21). Full
 *  liste hver gang (samme idiom som `TrackListener`) — hubben har allerede
 *  slått sammen delta+snapshot til én tilstand før dette kalles. */
export type SporUtenPosisjonListener = (spor: SporUtenPosisjon[]) => void;

// `CollabMessage` flyttet til `@skade/contracts` (QA-003) — se filhodet der
// for divergensen dette rettet: `onmessage` under bygde FØR meldingen
// felt-for-felt, og glemte `teig`/`teigId`/`rfiSvar`/`kalenderId`/`ts` —
// akkurat de feltene `main.ts` sin `onCollab`-handler leser. En teig-
// oppdatering, kalender-sletting eller RFI-svar fra hubben nådde WS-en, men
// ble kastet bort HER, før den nådde handleren. Reeksportert for
// bakoverkompatible imports (`import type { CollabMessage } from './feed.js'`).
export type { CollabMessage };

export interface Feed {
  start(): void;
  stop(): void;
  onTracks(fn: TrackListener): void;
  onHistory?(fn: HistoryListener): void;
  onCollab?(fn: CollabListener): void;
  /**
   * EMCON-nivået fra hubben (`{type:'emcon'}`).
   *
   * ⚠ EGEN KANAL, IKKE EN COLLAB-KIND. Collab-meldinger scope-filtreres per
   * mottaker; EMCON gjør det bevisst ikke — en operatør som ikke VET at det er
   * radiotaushet, bryter den. Se `sendAlle` i hubben.
   */
  onEmcon?(fn: (status: unknown) => void): void;
  /**
   * Sendekøens tilstand (UX-funn F7). Valgfri fordi `DemoFeed` ikke har noen kø
   * — den sender ingenting ut, og en tom kø-stripe over en demo ville vært en
   * påstand om samband som ikke finnes.
   */
  onKo?(fn: (st: KoStatus, tekst: string) => void): void;
  /**
   * Ferske uposisjonerte deteksjoner (VALE SENS-21). Valgfri av samme grunn
   * som `onKo`: `DemoFeed` genererer ingen (en tom stripe over en demo ville
   * vært en påstand om et ukalibrert kamera som ikke finnes).
   */
  onSporUtenPosisjon?(fn: SporUtenPosisjonListener): void;
  /**
   * Mottatt NACK — hubben forsto ikke et event vi nettopp sendte (ukjent kind
   * eller feilformet payload for en kjent kind, QA-003/RÅ-2). Aldri stille:
   * minst logges den, så en operatør ikke tror noe ble delt når det ikke ble.
   */
  onNack?(fn: (reason: string, mottatt: unknown) => void): void;
  /** Send et efemert collab-event (pointer/tegning/enheter/melding) til andre.
   * `CollabIn` er den DELTE konvolutt-typen (`@skade/contracts`) — se
   * filhodet i `@skade/contracts/collab.ts` for hvorfor den ikke lenger
   * hånddupliseres her. */
  send?(msg: CollabIn): void;
}

/** Samler siste kjente track per id (nyeste ts vinner) — fulliste ut. */
class TrackStore {
  private byId = new Map<string, Track>();
  ingestEvents(raw: unknown): Track[] {
    for (const ev of parseSensorBatch(raw)) {
      for (const t of sensorEventToTracks(ev)) {
        const cur = this.byId.get(t.id);
        if (!cur || t.tsMs >= cur.tsMs) this.byId.set(t.id, t);
      }
    }
    return this.list();
  }
  list(): Track[] {
    return [...this.byId.values()].sort((a, b) => b.tsMs - a.tsMs);
  }
}

/** WebSocket-feed mot SKADE Hub. Håndterer to former:
 *  - {type:'snapshot', tracks, history, sporUtenPosisjon}  → fusjonerte tracks
 *    fra hubben (foretrukket). `sporUtenPosisjon` (VALE SENS-21) er ferske
 *    deteksjoner fra ukalibrerte kilder — ALDRI en `Track` (lat/lon er
 *    obligatorisk der), levert i sin egen liste.
 *  - rått no.gna.spark.sensor-event/batch → normaliseres lokalt (fallback) */
export class LiveFeed implements Feed {
  private ws?: WebSocket;
  private store = new TrackStore();
  private listeners: TrackListener[] = [];
  private historyListeners: HistoryListener[] = [];
  private collabListeners: CollabListener[] = [];
  private authErrorListeners: (() => void)[] = [];
  // Full tilstand vedlikeholdt på tvers av 'snapshot' (ved tilkobling) + 'delta'
  // (hjerteslag). Delta-push: hubben sender bare ENDREDE spor + fjernede id-er.
  private snap = new Map<string, Track>();
  private histAll: Record<string, [number, number][]> = {};
  // Samme snapshot+delta-idiom som `snap`, men for uposisjonerte deteksjoner
  // (VALE SENS-21) — ingen `history`-motpart (ingen posisjon å tegne en bane av).
  private snapUpos = new Map<string, SporUtenPosisjon>();
  private uposListeners: SporUtenPosisjonListener[] = [];
  private backoff = new Backoff({ baseMs: 1000, maxMs: 30_000, factor: 2, jitter: 0.25 });
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private closedByUser = false;
  constructor(private url: string) {}
  onTracks(fn: TrackListener) { this.listeners.push(fn); }
  onHistory(fn: HistoryListener) { this.historyListeners.push(fn); }
  onCollab(fn: CollabListener) { this.collabListeners.push(fn); }
  onSporUtenPosisjon(fn: SporUtenPosisjonListener) { this.uposListeners.push(fn); }
  private emconListeners: ((s: unknown) => void)[] = [];
  onEmcon(fn: (s: unknown) => void) { this.emconListeners.push(fn); }
  private nackListeners: ((reason: string, mottatt: unknown) => void)[] = [];
  onNack(fn: (reason: string, mottatt: unknown) => void) { this.nackListeners.push(fn); }
  /** Sesjonen mangler/utløp (WS avvist med 4401) → klienten viser innlogging. */
  onAuthError(fn: () => void) { this.authErrorListeners.push(fn); }
  /**
   * SENDEKØ (UX-rapportens Fase 3, funn F7).
   *
   * ⚠ FØR STO DET BARE DETTE HER:
   *   `if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(...)`
   * — altså: er ikke socketen åpen, forsvinner meldingen. Ingen kø, ingen feil,
   * ingen beskjed. På mobilt nett kobler WebSocket-en til igjen hele tiden, så en
   * CONTACT sendt i det øyeblikket kunne bare bli borte mens operatøren satt igjen
   * med «✓ Sendt». `sendeko.ts` bærer resonnementet i sin helhet.
   */
  private ko = new Sendeko(nettlagring());
  private koLyttere: ((st: KoStatus, tekst: string) => void)[] = [];

  /** Abonner på køstatus. Flata viser dette — en stille kø er nesten like ille. */
  onKo(fn: (st: KoStatus, tekst: string) => void) { this.koLyttere.push(fn); }

  private meldKo() {
    const st = this.ko.status();
    const tekst = koTekst(st, Date.now());
    for (const l of this.koLyttere) l(st, tekst);
  }

  /** Ett sted som faktisk skriver på socketen. */
  private raaSend(msg: Record<string, unknown>): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    try { this.ws.send(JSON.stringify(msg)); return true; } catch { return false; }
  }

  /** Tøm køen. Kalles ved `onopen` og etter hvert vellykket send. */
  private toemKo() {
    const sendt = this.ko.toem((m) => this.raaSend(m));
    if (sendt) this.meldKo();
  }

  send(msg: CollabIn) {
    const m = msg as unknown as Record<string, unknown>;
    // ⚠ KØEN FØRST NÅR DEN IKKE ER TOM. Ellers ville en melding sendt rett etter
    // gjenoppkobling gått FØR de som har ventet i tjue minutter, og en rettelse
    // kunne ankommet før meldingen den retter.
    if (this.ko.status().ventende) {
      if (this.ko.leggIKo(m, Date.now())) { this.meldKo(); this.toemKo(); return; }
      // Efemer type med kø foran seg: send direkte om mulig, ellers dropp (riktig).
      this.raaSend(m);
      return;
    }
    if (this.raaSend(m)) return;
    // Sendingen gikk ikke. Efemert (`pointer`) skal tapes — en peker fra fire
    // minutter siden er FEIL informasjon, ikke forsinket. Alt annet holdes.
    if (this.ko.leggIKo(m, Date.now())) this.meldKo();
    else if (politikkFor(String(m.type ?? '')) !== 'droppes') {
      // Kan ikke skje med dagens politikk-tabell, men hvis den endres skal tapet
      // ikke bli stille.
      this.meldKo();
    }
  }
  start() {
    this.closedByUser = false;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = undefined; }
    this.ws = new WebSocket(this.url);
    // Vellykket tilkobling → nullstill backoff OG tøm sendekøen. Dette er
    // øyeblikket alt som har ventet skal gå ut.
    this.ws.onopen = () => { this.backoff.reset(); this.toemKo(); };
    // Hubben avviser uautentiserte WS med close-kode 4401 → be om innlogging.
    // Andre lukkinger (nett faller/hub restarter) → auto-reconnect m/backoff.
    this.ws.onclose = (e) => {
      if (e.code === 4401) { this.authErrorListeners.forEach((l) => l()); return; }
      if (this.closedByUser) return;
      const delay = this.backoff.next();
      this.reconnectTimer = setTimeout(() => this.start(), delay);
    };
    this.ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data as string);
        if (msg && msg.type === 'snapshot' && Array.isArray(msg.tracks)) {
          this.snap = new Map((msg.tracks as Track[]).map((t) => [t.id, t]));
          this.histAll = (msg.history as Record<string, [number, number][]>) ?? {};
          this.listeners.forEach((l) => l(msg.tracks as Track[]));
          if (msg.history) this.historyListeners.forEach((l) => l(this.histAll));
          // VALE SENS-21: samme full-sett-idiom som tracks ved førstegangs-snapshot.
          if (Array.isArray(msg.sporUtenPosisjon)) {
            this.snapUpos = new Map((msg.sporUtenPosisjon as SporUtenPosisjon[]).map((s) => [s.id, s]));
            this.uposListeners.forEach((l) => l([...this.snapUpos.values()]));
          }
        } else if (msg && msg.type === 'delta') {
          // Flett endrede spor inn i full-tilstanden; slett fjernede id-er.
          for (const t of (msg.tracks as Track[]) ?? []) this.snap.set(t.id, t);
          for (const id of (msg.removed as string[]) ?? []) { this.snap.delete(id); delete this.histAll[id]; }
          if (msg.history) Object.assign(this.histAll, msg.history);
          const list = [...this.snap.values()].sort((a, b) => b.tsMs - a.tsMs);
          this.listeners.forEach((l) => l(list));
          if (msg.history || (msg.removed && msg.removed.length)) this.historyListeners.forEach((l) => l(this.histAll));
          // VALE SENS-21: samme flett+slett-idiom, egen liste (ingen `history` her).
          const uposChanged = (msg.sporUtenPosisjon as SporUtenPosisjon[]) ?? [];
          const uposRemoved = (msg.removedSporUtenPosisjon as string[]) ?? [];
          if (uposChanged.length || uposRemoved.length) {
            for (const s of uposChanged) this.snapUpos.set(s.id, s);
            for (const id of uposRemoved) this.snapUpos.delete(id);
            this.uposListeners.forEach((l) => l([...this.snapUpos.values()]));
          }
        } else if (msg && msg.type === 'emcon') {
          this.emconListeners.forEach((l) => { try { l(msg.emcon); } catch { /* én lytter skal ikke velte de andre */ } });
        } else if (msg && msg.type === 'nack') {
          // QA-003/RÅ-2: hubben forsto ikke et event vi sendte. Aldri stille —
          // logges alltid (console.warn), i tillegg til at abonnenter varsles.
          const nack = parseCollabNack(msg);
          if (nack) {
            // eslint-disable-next-line no-console
            console.warn('[collab] NACK fra hub:', nack.reason, nack.mottatt);
            this.nackListeners.forEach((l) => { try { l(nack.reason, nack.mottatt); } catch { /* én lytter skal ikke velte de andre */ } });
          }
        } else if (msg && msg.type === 'collab') {
          // ⚠ HELE `CollabMessage`-formen forwardes (ikke felt-for-felt hånd-
          // plukket) — det var nettopp en hånd-plukket liste som glemte
          // `teig`/`teigId`/`rfiSvar`/`kalenderId`/`ts` som forårsaket QA-003.
          const { type: _type, ...melding } = msg as CollabMessage & { type: 'collab' };
          this.collabListeners.forEach((l) => l(melding));
        } else {
          const tracks = this.store.ingestEvents(msg);
          this.listeners.forEach((l) => l(tracks));
        }
      } catch {
        /* ugyldig melding ignoreres — aldri crash på feltdata */
      }
    };
  }
  stop() {
    this.closedByUser = true;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = undefined; }
    this.ws?.close();
  }
}

/**
 * Demo-feed: ett SPARK-lag i Follo-området med en drone-deteksjon (modus B,
 * løst punkt) og et skudd (modus A, peiling+avstand). Alt merket source:'demo'
 * via node_id-prefiks slik at ingen forveksler det med ekte data.
 */
export class DemoFeed implements Feed {
  private store = new TrackStore();
  private listeners: TrackListener[] = [];
  private timer?: ReturnType<typeof setInterval>;
  onTracks(fn: TrackListener) {
    this.listeners.push(fn);
  }
  start() {
    this.emit();
    this.timer = setInterval(() => this.emit(), 4000);
  }
  stop() {
    if (this.timer) clearInterval(this.timer);
  }
  private emit() {
    const now = Date.now();
    // Liten «patrulje»-drift på node-posisjonen.
    const jitter = (Math.sin(now / 8000) * 0.002);
    const events = [
      { v: 1, node_id: 'demo-1', kind: 'position', ts: now,
        pos: { lat: 59.746 + jitter, lon: 10.836 }, batt_pct: 82 },
      { v: 1, node_id: 'demo-2', kind: 'detection', ts: now,
        pos: { lat: 59.752, lon: 10.851 },
        detection: { type: 'drone', confidence: 0.88,
          target: { lat: 59.7585, lon: 10.8625, cep_m: 60 }, count: 1 } },
      { v: 1, node_id: 'demo-3', kind: 'alarm', ts: now,
        pos: { lat: 59.740, lon: 10.820 },
        detection: { type: 'gunshot', confidence: 0.74, bearing_deg: 55, range_m: 700, count: 3 } },
    ];
    const tracks = this.store.ingestEvents(events);
    this.listeners.forEach((l) => l(tracks));
  }
}
